import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuController, ModalController, ToastController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth.service';
import { CloudfunctriggersService } from 'src/app/services/cloudfunctriggers.service';
import { DateTimeService } from 'src/app/services/date-time.service';
import { VouchersService } from 'src/app/services/vouchers.service';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { ToastServiceService } from 'src/app/services/toast-service.service';
import { BarcodeScannerService } from 'src/app/services/barcode-scanner.service';
import { AlertService } from 'src/app/services/alert.service';
import { WalletLoyaltyCard, Voucher, WalletCardInfo, IndefiniteLoyaltyProgramme, CampaignLoyaltyProgramme, ParticipantCoupon, IndefiniteCoupon, WalletCouponCardInfo, ForSaleCoupon } from 'src/app/models/interfaces';
import { LocationService } from 'src/app/services/location.service';
import { AssetlinksService } from 'src/app/services/assetlinks.service';
import { LoyaltyCardService } from 'src/app/services/loyalty-card.service';
import { map } from 'rxjs';
import { TakealotVouchersService } from 'src/app/services/takealot-vouchers.service';
import { CouponsService } from 'src/app/services/coupons.service';
import { PushNotificationsService } from 'src/app/services/pushnotifications.service';


@Component({
  selector: 'app-wallet',
  templateUrl: './wallet.page.html',
  styleUrls: ['./wallet.page.scss'],
})
export class WalletPage implements OnInit {

  readonly scanRange = 0.100

  /**
   * some loyaltyCards and vouchers can fail to render
   * because their corresponding special can not be found.
   * This object represents the actual cards that could be rendered
   */
  renderedCards = {
    vouchers: new Map<string, WalletCardInfo>(),
    loyaltyCards: new Map<string, WalletCardInfo>(),
    coupons: new Map<string, WalletCouponCardInfo>()
  }

  /**
   * this object represents the fetched specials of
   * the rendered vouchers and loyaltyCards
   */
  cardListings = {
    loyaltyCardSpecials: new Map<string, CampaignLoyaltyProgramme | IndefiniteLoyaltyProgramme>(),
    voucherCardSpecials: new Map<string, CampaignLoyaltyProgramme | IndefiniteLoyaltyProgramme>(),
    couponCardListings: new Map<string, IndefiniteCoupon|ForSaleCoupon>()
  }
  
  @Input() initialSegment?:'cards'|'vouchers'|'rewards'|'coupons'
  quickfilter:'vouchers'|'cards'|'rewards'|'coupons'='cards';

  groupedVouchers$ = this.voucherService.userVouchers$.pipe(
    map((vouchers) => {
      const uniqueVouchers = vouchers.reduce((accumulator, voucher) => {
        const existingVoucherIndex = accumulator.findIndex(
          (v) => v.voucher.specialUid === voucher.specialUid
        );
  
        if (existingVoucherIndex === -1) {
          accumulator.push({
            voucher,
            expiry: voucher.expirationDate_unix_utc, 
            count: 1
          });
        } else {
          accumulator[existingVoucherIndex].count++;
          const existingExpiry = accumulator[existingVoucherIndex].expiry;
          const currentExpiry = voucher.expirationDate_unix_utc;
  
          if (currentExpiry < existingExpiry) {
            accumulator[existingVoucherIndex] = {
              voucher,
              expiry: currentExpiry,
              count: accumulator[existingVoucherIndex].count
            };
          }
        }
  
        return accumulator;
      }, []);
  
      return uniqueVouchers.map(({ voucher, count }) => ({
        voucher,
        count
      }));
    })
  );

  groupedCoupons$ = this.couponService.userCoupons$.pipe(
    map((coupons) => {
      const uniqueCoupons = coupons.reduce((accumulator, coupon) => {
        const existingVoucherIndex = accumulator.findIndex(
          (v) => v.coupon.outlet_group_coupon_id === coupon.outlet_group_coupon_id
        );
  
        if (existingVoucherIndex === -1) {
          accumulator.push({
            coupon,
            expiry: coupon.expirationDate_unix_utc, 
            count: 1
          });
        } else {
          accumulator[existingVoucherIndex].count++;
          const existingExpiry = accumulator[existingVoucherIndex].expiry;
          const currentExpiry = coupon.expirationDate_unix_utc;
  
          if (currentExpiry < existingExpiry) {
            accumulator[existingVoucherIndex] = {
              coupon,
              expiry: currentExpiry,
              count: accumulator[existingVoucherIndex].count
            };
          }
        }
  
        return accumulator;
      }, []);
  
      return uniqueCoupons.map(({ coupon, count }) => ({
        coupon,
        count
      }));
    })
  );

  constructor(
    public menuController:MenuController,
    public assetLinksService:AssetlinksService,
    public authService:AuthService,
    public takealotVouchersService:TakealotVouchersService,
    public dateTimeService:DateTimeService,
    public notifications:PushNotificationsService,
    public modalCtrl:ModalController,
    public loyaltyCardService:LoyaltyCardService,
    public voucherService:VouchersService,
    public couponService:CouponsService,
    public barcodeScannerService:BarcodeScannerService,
    public locationService:LocationService,
    public router: Router,
    public toastCrtl: ToastController,
    public cloudTriggers: CloudfunctriggersService,
    public network: NetworkStatusService,
    public toasts: ToastServiceService,
    public alertService:AlertService,
  ) {

  }

  async ngOnInit() {
    if(!this.initialSegment){
      this.initialSegment = 'cards'
    }
    await this.changequickfilter({target:{
      value:this.initialSegment
    }})
  }

  addToVoucherSpecials(special: CampaignLoyaltyProgramme | IndefiniteLoyaltyProgramme) {
    this.cardListings.voucherCardSpecials.set(special.id, special)
  }

  addToCouponListings(couponListing: IndefiniteCoupon|ForSaleCoupon) {
    this.cardListings.couponCardListings.set(couponListing.id, couponListing)
  }

  addToLoyaltyCardSpecials(special:CampaignLoyaltyProgramme | IndefiniteLoyaltyProgramme){
    this.cardListings.loyaltyCardSpecials.set(special.id,special)
  }

  updateVoucherCardMap(cardInfo:WalletCardInfo){
    this.renderedCards.vouchers.set(cardInfo.id,cardInfo)
  }

  updateCouponCardMap(cardInfo:WalletCouponCardInfo){
    this.renderedCards.coupons.set(cardInfo.id,cardInfo)
  }

  updateLoyaltyCardMap(cardInfo:WalletCardInfo){
    this.renderedCards.loyaltyCards.set(cardInfo.id,cardInfo)
    this.sortLoyaltyCardsBasedOnShopNameName(this.loyaltyCardService.loyaltyCards)
  }

  giveSpecialBasedOnLoyaltyCard(loyaltyCard: WalletLoyaltyCard) {
    return this.cardListings.loyaltyCardSpecials.get(loyaltyCard.special_id);
  }

  giveSpecialBasedOnVoucher(voucher: Voucher) {
    return this.cardListings.voucherCardSpecials.get(voucher.specialUid);
  }

  giveCouponListingBasedOnUserCoupon(coupon: ParticipantCoupon) {
    return this.cardListings.couponCardListings.get(coupon.outlet_group_coupon_id);
  }
  

  async changequickfilter(e) {
    switch (e.target.value) {
      case 'vouchers':
        this.quickfilter = 'vouchers'
        break;
      case 'cards':
        this.quickfilter = 'cards'
        break;
      case 'rewards':
        this.quickfilter = 'rewards'
        break;
      case 'coupons':
        this.quickfilter = 'coupons'
        break;
      default:
        console.log('error - tab does not exist');
        break;
    }
  }

  async openMenu() {
    await this.menuController.open();
  }

  async back() {
    await this.modalCtrl.dismiss();
  }
  
  private sortLoyaltyCardsBasedOnShopNameName(loyaltyCards:WalletLoyaltyCard[]) {
    loyaltyCards.sort((a, b) => {
      const cardA = this.renderedCards.loyaltyCards.get(a.id);
      const cardB = this.renderedCards.loyaltyCards.get(b.id);
      if (cardA && cardB) {
        return cardA.shopName.localeCompare(cardB.shopName);
      } else {
        return 0;
      }
    });
  }
}
